import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Plus, 
  Trash2, 
  ChevronDown, 
  ChevronUp, 
  ArrowLeft,
  Pencil,
  X
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import ItineraryContactModal from './modals/ItineraryContactModal';
import TicketModal from './modals/TicketModal';
import ParticularModal from './modals/ParticularModal';
import ViewContactModal from './modals/ViewContactModal';
import ViewEventModal from './modals/ViewEventModal';
import ViewTicketModal from './modals/ViewTicketModal';

// Helper to format date cleanly as "Mon, 12 Oct"
const formatDayDate = (dateStr, startDate, dayIdx) => {
  let d = null;
  if (dateStr) {
    const cleaned = String(dateStr).replace(/(\d+)(st|nd|rd|th)/g, "$1");
    const parsed = new Date(cleaned);
    if (!isNaN(parsed.getTime())) d = parsed;
  }
  if (!d && startDate) {
    const start = new Date(startDate);
    if (!isNaN(start.getTime())) {
      d = new Date(start);
      d.setDate(d.getDate() + dayIdx);
    }
  }
  if (!d) return dateStr || '';

  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]}`;
};

const ItineraryTableView = ({
  formData,
  setFormData,
  setLocalFormData,
  formVoucherTheme = {},
  setFormVoucherTheme,
  addItineraryDay,
  headerInfo, // Optional: { bookingId, guestName, destination, totalDays, startDate, endDate, dashboardPath }
  isEditingItinerary: externalIsEditingItinerary,
  setIsEditingItinerary: externalSetIsEditingItinerary
}) => {
  const navigate = useNavigate();
  // State for which days have expanded their remaining particulars
  // Default: empty object => every day initially shows ONLY ONE particular
  const [expandedDayParticulars, setExpandedDayParticulars] = useState({});

  // State for which days have expanded their remaining drivers
  // Default: empty object => every day initially shows ONLY ONE driver
  const [expandedDayDrivers, setExpandedDayDrivers] = useState({});



  // State for which days have expanded their remaining tickets
  // Default: empty object => every day initially shows ONLY ONE ticket
  const [expandedDayTickets, setExpandedDayTickets] = useState({});

  // Active Modals state
  const [contactModalState, setContactModalState] = useState(null); // { dayIdx, dayNumber, initialContact }
  const [ticketModalState, setTicketModalState] = useState(null); // { dayIdx, dayNumber, initialTicket }
  const [particularModalState, setParticularModalState] = useState(null); // { dayIdx, evtIdx, isNew, currentParticular }
  const [viewContactModalState, setViewContactModalState] = useState(null); // contact object
  const [viewEventModalState, setViewEventModalState] = useState(null); // event object
  const [viewTicketModalState, setViewTicketModalState] = useState(null); // ticket object

  // Edit mode state: controlled from FullItineraryPage or fallback to local state
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const isEditingItinerary = externalIsEditingItinerary !== undefined ? externalIsEditingItinerary : internalIsEditing;
  const setIsEditingItinerary = externalSetIsEditingItinerary || setInternalIsEditing;

  const itineraryDays = formData.itinerary || [];

  const toggleDayParticulars = (dayIdx) => {
    setExpandedDayParticulars(prev => ({
      ...prev,
      [dayIdx]: !prev[dayIdx]
    }));
  };

  const toggleDayDrivers = (dayIdx) => {
    setExpandedDayDrivers(prev => ({
      ...prev,
      [dayIdx]: !prev[dayIdx]
    }));
  };

  const toggleDayTickets = (dayIdx) => {
    setExpandedDayTickets(prev => ({
      ...prev,
      [dayIdx]: !prev[dayIdx]
    }));
  };

  // Helper to extract drivers associated with a day (Drivers ONLY in this column)
  const getDayDrivers = (day, dayIdx) => {
    const dayNum = day.day_number || (dayIdx + 1);
    const dayEventIds = (day.events || []).map(e => e.id || e._id);
    const driversMap = new Map();

    // 1. From formData.contact_details - ONLY DRIVERS!
    (formData.contact_details || []).forEach(c => {
      const roleStr = (c.label || c.role || '').toLowerCase();
      const isDriver = roleStr === 'driver';
      if (!isDriver) return; // Exclude Guide, Helper, Tour Manager, Custom, Hotel

      const cDay = Number(c.day_number);
      const isWholeTrip = c.assignment_type === 'whole_trip' || c.date === 'Whole Trip' || c.date === 'All Days' || (!cDay && !c.event_id);
      const isForThisDay = isWholeTrip || cDay === dayNum;
      const isForThisEvent = c.event_id && dayEventIds.includes(c.event_id);

      if (isForThisDay || isForThisEvent) {
        const id = c.id || c._id || `cnt_${c.name}_${dayNum}`;
        driversMap.set(id, { ...c, id });
      }
    });

    // 2. Legacy fallback from events
    (day.events || []).forEach(evt => {
      if (evt.driver) {
        const legacyId = `leg_drv_${evt.id || dayIdx}`;
        const alreadyExists = Array.from(driversMap.values()).some(
          c => c.name === evt.driver.name && (c.event_id === evt.id || c.assignment_type === 'whole_day')
        );
        if (!alreadyExists) {
          driversMap.set(legacyId, {
            id: legacyId,
            name: evt.driver.name || (typeof evt.driver === 'string' ? evt.driver : 'Driver'),
            number: evt.driver.phone || '',
            label: 'Driver',
            role: 'Driver',
            vehicle_type: evt.driver.vehicle || '',
            vehicle_number: evt.driver.vehicle_number || '',
            assignment_type: 'event',
            event_id: evt.id,
            event_title: evt.title || evt.activity || 'Event',
            day_number: dayNum,
            pickup_location: evt.pickup_drop?.pickup || (evt.pickup_drop?.pickup_location ? { name: evt.pickup_drop.pickup_location } : null),
            drop_location: evt.pickup_drop?.drop || (evt.pickup_drop?.drop_location ? { name: evt.pickup_drop.drop_location } : null),
            pickup_time: evt.pickup_drop?.pickup_time || '',
            drop_time: evt.pickup_drop?.drop_time || ''
          });
        }
      }
    });

    return Array.from(driversMap.values());
  };

  // Helper to extract tickets associated with a day
  const getDayTickets = (day, dayIdx) => {
    const dayNum = day.day_number || (dayIdx + 1);
    const ticketsMap = new Map();

    // 1. Tickets from events
    (day.events || []).forEach(evt => {
      if (Array.isArray(evt.tickets)) {
        evt.tickets.forEach(t => {
          if (t && (t.id || t._id || t.name)) {
            const tId = t.id || t._id || `tkt_${t.name}_${dayNum}`;
            if (!ticketsMap.has(tId)) {
              ticketsMap.set(tId, { ...t, id: tId });
            }
          }
        });
      } else if (evt.ticket) {
        const tId = evt.ticket.id || evt.ticket._id || `leg_tkt_${evt.id || dayIdx}`;
        if (!ticketsMap.has(tId)) {
          ticketsMap.set(tId, {
            ...evt.ticket,
            id: tId,
            day_number: dayNum,
            assigned_event_titles: [evt.title || evt.activity || 'Event']
          });
        }
      }
    });

    // 2. Also check important_documents synced tickets for this day
    (formData.important_documents || []).forEach(doc => {
      if (doc.ticket_id && doc.day_number === dayNum) {
        if (!ticketsMap.has(doc.ticket_id)) {
          ticketsMap.set(doc.ticket_id, {
            id: doc.ticket_id,
            name: doc.name,
            url: doc.url,
            file_name: doc.filename,
            day_number: doc.day_number,
            assigned_events: doc.event_ids || [],
            assigned_event_titles: doc.event_title ? [doc.event_title] : []
          });
        }
      }
    });

    return Array.from(ticketsMap.values());
  };

  // Contact Handlers
  const handleSaveContact = (dayIdx, contactData, isEdit) => {
    setFormData(prev => {
      const newContacts = [...(prev.contact_details || [])];
      const matchIdx = newContacts.findIndex(c => (c.id || c._id) === contactData.id);
      if (matchIdx >= 0) {
        newContacts[matchIdx] = contactData;
      } else {
        newContacts.push(contactData);
      }

      // Sync into itinerary events for backward compatibility
      const newItin = [...(prev.itinerary || [])];
      const targetDayIdx = typeof dayIdx === 'number' && dayIdx >= 0
        ? dayIdx
        : (contactData.day_number ? newItin.findIndex((d, idx) => (d.day_number || (idx + 1)) === Number(contactData.day_number)) : -1);

      if (targetDayIdx >= 0 && newItin[targetDayIdx]) {
        const targetDay = { ...newItin[targetDayIdx] };
        const targetEvents = [...(targetDay.events || [])];

        if (contactData.label === 'Driver') {
          const driverObj = {
            name: contactData.name,
            phone: contactData.number,
            vehicle: contactData.vehicle_type,
            vehicle_number: contactData.vehicle_number
          };

          if (contactData.assignment_type === 'event' && contactData.event_id) {
            const evtIdx = targetEvents.findIndex(e => (e.id || e._id) === contactData.event_id);
            if (evtIdx >= 0) {
              const updatedEvt = { ...targetEvents[evtIdx], driver: driverObj };
              if (contactData.pickup_location || contactData.drop_location) {
                updatedEvt.pickup_drop = {
                  pickup: contactData.pickup_location,
                  drop: contactData.drop_location,
                  pickup_time: contactData.pickup_time || null,
                  drop_time: contactData.drop_time || null
                };
              }
              targetEvents[evtIdx] = updatedEvt;
            }
          } else if (contactData.assignment_type === 'whole_day') {
            targetDay.driver = driverObj;
          }
        }

        targetDay.events = targetEvents;
        newItin[targetDayIdx] = targetDay;
      }

      return {
        ...prev,
        contact_details: newContacts,
        itinerary: newItin
      };
    });
  };

  const handleDeleteContact = (dayIdx, contactId) => {
    setFormData(prev => {
      const deletedContact = (prev.contact_details || []).find(c => (c.id || c._id) === contactId);
      const newContacts = (prev.contact_details || []).filter(c => (c.id || c._id) !== contactId);

      const newItin = [...(prev.itinerary || [])];
      const targetDayIdx = typeof dayIdx === 'number' && dayIdx >= 0
        ? dayIdx
        : (deletedContact?.day_number ? newItin.findIndex((d, idx) => (d.day_number || (idx + 1)) === Number(deletedContact.day_number)) : -1);

      if (targetDayIdx >= 0 && newItin[targetDayIdx] && newItin[targetDayIdx].events) {
        const targetDay = { ...newItin[targetDayIdx] };
        const targetEvents = targetDay.events.map(evt => {
          if (deletedContact && deletedContact.event_id === (evt.id || evt._id)) {
            return {
              ...evt,
              driver: null,
              pickup_drop: null
            };
          }
          return evt;
        });
        targetDay.events = targetEvents;
        if (deletedContact && deletedContact.assignment_type === 'whole_day') {
          targetDay.driver = null;
        }
        newItin[targetDayIdx] = targetDay;
      }

      return {
        ...prev,
        contact_details: newContacts,
        itinerary: newItin
      };
    });
    toast.success('Contact removed.');
  };

  // Ticket Handlers
  const handleSaveTicket = (dayIdx, ticketData, isEdit) => {
    const dayNum = (formData.itinerary && formData.itinerary[dayIdx]?.day_number) || (dayIdx + 1);

    setFormData(prev => {
      const newItin = [...(prev.itinerary || [])];
      const targetDay = { ...newItin[dayIdx] };
      const targetEvents = [...(targetDay.events || [])];

      const assignedIds = Array.isArray(ticketData.assigned_events) ? ticketData.assigned_events : [];
      
      const updatedEvents = targetEvents.map(evt => {
        const evtId = evt.id || evt._id;
        const currentTickets = Array.isArray(evt.tickets) ? [...evt.tickets] : (evt.ticket ? [evt.ticket] : []);

        if (assignedIds.includes(evtId) || assignedIds.length === 0) {
          const tIdx = currentTickets.findIndex(t => (t.id || t._id) === ticketData.id);
          if (tIdx >= 0) {
            currentTickets[tIdx] = ticketData;
          } else {
            currentTickets.push(ticketData);
          }
          return {
            ...evt,
            tickets: currentTickets,
            ticket: currentTickets[0] || ticketData
          };
        } else {
          const filtered = currentTickets.filter(t => (t.id || t._id) !== ticketData.id);
          return {
            ...evt,
            tickets: filtered,
            ticket: filtered[0] || null
          };
        }
      });

      targetDay.events = updatedEvents;
      newItin[dayIdx] = targetDay;

      // Synchronize with Important Documents
      const assignedLabel = ticketData.assigned_event_titles?.length > 0
        ? `Day ${dayNum} · ${ticketData.assigned_event_titles.join(', ')}`
        : `Day ${dayNum}`;

      const docName = `${ticketData.name} — ${assignedLabel}`;
      const docEntry = {
        ticket_id: ticketData.id,
        name: docName,
        filename: ticketData.file_name || ticketData.name,
        url: ticketData.url || '',
        uploaded_at: new Date(),
        day_number: dayNum,
        event_ids: assignedIds,
        event_title: ticketData.assigned_event_titles?.join(', ') || '',
        assignment_label: assignedLabel
      };

      const existingDocs = [...(prev.important_documents || [])];
      const docIdx = existingDocs.findIndex(d => d.ticket_id === ticketData.id);
      if (docIdx >= 0) {
        existingDocs[docIdx] = { ...existingDocs[docIdx], ...docEntry };
      } else {
        existingDocs.push(docEntry);
      }

      return {
        ...prev,
        itinerary: newItin,
        important_documents: existingDocs
      };
    });
  };

  const handleRemoveTicket = (dayIdx, ticketId) => {
    setFormData(prev => {
      const newItin = [...(prev.itinerary || [])];
      const targetDay = { ...newItin[dayIdx] };
      if (targetDay && targetDay.events) {
        targetDay.events = targetDay.events.map(evt => {
          const currentTickets = Array.isArray(evt.tickets) ? evt.tickets : (evt.ticket ? [evt.ticket] : []);
          const filtered = currentTickets.filter(t => (t.id || t._id) !== ticketId);
          return {
            ...evt,
            tickets: filtered,
            ticket: filtered[0] || null
          };
        });
        newItin[dayIdx] = targetDay;
      }

      const updatedDocs = (prev.important_documents || []).filter(d => d.ticket_id !== ticketId);

      return {
        ...prev,
        itinerary: newItin,
        important_documents: updatedDocs
      };
    });
    toast.success('Ticket removed.');
  };

  // Particulars CRUD
  const handleSaveParticular = (particularData) => {
    if (!particularModalState) return;
    const { dayIdx, evtIdx, isNew } = particularModalState;

    setFormData(prev => {
      const newItin = [...(prev.itinerary || [])];
      const targetDay = { ...newItin[dayIdx] };
      const targetEvents = [...(targetDay.events || [])];

      if (isNew) {
        const newEvt = {
          id: `evt_${dayIdx + 1}_${Date.now()}`,
          title: particularData.title,
          activity: particularData.title,
          time: particularData.time,
          description: particularData.description,
          source: 'manual',
          driver: targetDay.driver || null,
          ticket: null,
          pickup_drop: null
        };
        targetEvents.push(newEvt);
      } else {
        targetEvents[evtIdx] = {
          ...targetEvents[evtIdx],
          title: particularData.title,
          activity: particularData.title,
          time: particularData.time,
          description: particularData.description
        };
      }

      targetDay.events = targetEvents;
      targetDay.remarks = targetEvents.map(e => e.title || e.activity).filter(Boolean);
      targetDay.timings = targetEvents.map(e => e.time || '');
      newItin[dayIdx] = targetDay;
      return { ...prev, itinerary: newItin };
    });

    setExpandedDayParticulars(prev => ({ ...prev, [dayIdx]: true }));
    toast.success(isNew ? 'New event added!' : 'Event updated!');
  };

  const handleDeleteParticular = (dayIdx, evtIdx) => {
    setFormData(prev => {
      const newItin = [...(prev.itinerary || [])];
      const targetDay = { ...newItin[dayIdx] };
      const targetEvents = (targetDay.events || []).filter((_, i) => i !== evtIdx);
      targetDay.events = targetEvents;
      targetDay.remarks = targetEvents.map(e => e.title || e.activity).filter(Boolean);
      targetDay.timings = targetEvents.map(e => e.time || '');
      newItin[dayIdx] = targetDay;
      return { ...prev, itinerary: newItin };
    });
    toast.success('Event removed.');
  };

  const handleRemoveDay = (dayIdx) => {
    setFormData(prev => ({
      ...prev,
      itinerary: (prev.itinerary || []).filter((_, i) => i !== dayIdx)
    }));
    toast.success(`Day ${dayIdx + 1} removed.`);
  };

  const handleDayTitleChange = (dayIdx, newTitle) => {
    const updater = prev => {
      const newItin = [...(prev.itinerary || [])];
      newItin[dayIdx] = {
        ...newItin[dayIdx],
        day_title: newTitle,
        title: newTitle
      };
      return { ...prev, itinerary: newItin };
    };

    if (setLocalFormData) {
      setLocalFormData(updater);
    } else {
      setFormData(updater);
    }
  };

  const handleDayTitleBlur = (dayIdx) => {
    if (setLocalFormData) {
      setFormData(prev => prev);
      toast.success('Day title updated');
    }
  };

  return (
    <div className="w-full space-y-3">
      {/* Top Controls Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 pb-1">
        {headerInfo ? (
          <div className="flex items-center gap-2.5 min-w-0 w-full sm:w-auto">
            {headerInfo.dashboardPath && (
              <button
                onClick={() => navigate(headerInfo.dashboardPath)}
                className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-500 hover:text-slate-700 transition-all cursor-pointer shrink-0 shadow-2xs"
                title="Back to Dashboard"
              >
                <ArrowLeft size={14} />
              </button>
            )}
            <h1 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight truncate">
              {headerInfo.guestName}'s Full Itinerary
            </h1>
          </div>
        ) : (
          <div />
        )}

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2.5 sm:gap-4 w-full sm:w-auto shrink-0">
          {!isEditingItinerary ? (
            <div className="order-1 flex items-center gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setIsEditingItinerary(true)}
                className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 px-3.5 py-1.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200/90 rounded-lg font-medium text-xs shadow-2xs transition-all active:scale-95 cursor-pointer whitespace-nowrap"
              >
                <Pencil size={13} className="text-slate-500" />
                <span>Edit Itinerary</span>
              </button>
              <button
                type="button"
                onClick={() => setContactModalState({
                  mode: 'general',
                  dayIdx: 0,
                  dayNumber: 1,
                  initialContact: null
                })}
                className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 px-3.5 py-1.5 bg-[#067978] hover:bg-[#056362] text-white rounded-lg font-medium text-xs shadow-2xs transition-all active:scale-95 cursor-pointer whitespace-nowrap"
              >
                <Plus size={13} />
                <span>Add Contact</span>
              </button>
            </div>
          ) : (
            <div className="order-1 flex items-center gap-2 w-full sm:w-auto flex-wrap sm:flex-nowrap">
              <button
                type="button"
                onClick={() => setIsEditingItinerary(false)}
                className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200/90 rounded-lg font-medium text-xs shadow-2xs transition-all active:scale-95 cursor-pointer whitespace-nowrap"
              >
                <X size={13} className="text-slate-500" />
                <span>Close Editing</span>
              </button>

              <button
                type="button"
                onClick={() => setContactModalState({
                  mode: 'general',
                  dayIdx: 0,
                  dayNumber: 1,
                  initialContact: null
                })}
                className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200/90 rounded-lg font-medium text-xs shadow-2xs transition-all active:scale-95 cursor-pointer whitespace-nowrap"
              >
                <Plus size={13} className="text-slate-500" />
                <span>Add Contact</span>
              </button>

              <button
                type="button"
                onClick={addItineraryDay}
                className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 px-3.5 py-1.5 bg-[#067978] hover:bg-[#056564] text-white rounded-lg font-medium text-xs shadow-2xs transition-all active:scale-95 cursor-pointer whitespace-nowrap"
              >
                <Plus size={13} />
                <span>Add Day</span>
              </button>
            </div>
          )}

          <div className="order-2 sm:order-none flex items-center gap-2">
            <span className="text-xs font-medium text-slate-600 select-none">Include in PDF</span>
            <button
              type="button"
              role="switch"
              aria-checked={formVoucherTheme?.include_itinerary !== false}
              onClick={() => setFormVoucherTheme(prev => ({
                ...prev,
                include_itinerary: !(prev?.include_itinerary !== false)
              }))}
              className={`w-9 h-5 rounded-full transition-colors p-0.5 flex items-center shrink-0 cursor-pointer ${
                formVoucherTheme?.include_itinerary !== false ? 'bg-[#067978]' : 'bg-slate-300'
              }`}
            >
              <span
                className={`w-4 h-4 bg-white rounded-full transition-transform shadow-xs ${
                  formVoucherTheme?.include_itinerary !== false ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Main Full-Width Table */}
      <div className="bg-white rounded-2xl border border-slate-200/90 shadow-2xs overflow-hidden w-full">
        <div className="overflow-x-auto w-full no-scrollbar">
          <table className="w-full text-left border-collapse table-fixed min-w-[760px]">
            {/* Table Header: 4 Columns */}
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-normal text-slate-500 uppercase tracking-wider select-none">
                <th className="py-3.5 px-6 w-[18%]">Itinerary</th>
                <th className="py-3.5 px-6 w-[36%]">Events</th>
                <th className="py-3.5 px-6 w-[23%]">Driver</th>
                <th className="py-3.5 px-6 w-[23%]">Tickets</th>
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="divide-y divide-slate-100">
              {itineraryDays.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-12 text-center bg-slate-50/30">
                    <Calendar size={32} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-xs font-medium text-slate-700">No itinerary days yet</p>
                    <p className="text-[11px] text-slate-400 mt-0.5 mb-3">Start planning the schedule for your guests</p>
                    <button
                      type="button"
                      onClick={() => {
                        addItineraryDay();
                        setIsEditingItinerary(true);
                      }}
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-[#067978] text-white rounded-lg text-xs font-medium cursor-pointer"
                    >
                      <Plus size={13} /> Add Day 1
                    </button>
                  </td>
                </tr>
              ) : (
                itineraryDays.map((day, dayIdx) => {
                  const dayNum = day.day_number || dayIdx + 1;
                  const dayEvents = Array.isArray(day.events) && day.events.length > 0
                    ? day.events
                    : [{
                        id: `evt_${dayNum}_1`,
                        title: day.day_title || day.title || `Day ${dayNum} Sightseeing`,
                        activity: day.day_title || day.title || `Day ${dayNum} Sightseeing`,
                        time: (day.timings && day.timings[0]) || '10:00',
                        description: day.summary || '',
                        source: 'itinerary'
                      }];

                  const isExpanded = Boolean(expandedDayParticulars[dayIdx]);
                  const visibleEvents = isExpanded ? dayEvents : dayEvents.slice(0, 1);
                  const hiddenCount = dayEvents.length - 1;
                  const formattedDate = formatDayDate(day.date, formData.tour_info?.start_date, dayIdx);
                  const dayDrivers = getDayDrivers(day, dayIdx);
                  const isDriversExpanded = Boolean(expandedDayDrivers[dayIdx]);
                  const visibleDrivers = isDriversExpanded ? dayDrivers : dayDrivers.slice(0, 1);
                  const hiddenDriversCount = dayDrivers.length - 1;
                  const dayTickets = getDayTickets(day, dayIdx);
                  const isTicketsExpanded = Boolean(expandedDayTickets[dayIdx]);
                  const visibleTickets = isTicketsExpanded ? dayTickets : dayTickets.slice(0, 1);
                  const hiddenTicketsCount = dayTickets.length - 1;
                  const hasMultipleEvents = dayEvents.length > 1;
                  const hasMultipleDrivers = dayDrivers.length > 1;
                  const hasMultipleTickets = dayTickets.length > 1;
                  const hasFooterBar = hasMultipleEvents || hasMultipleDrivers || hasMultipleTickets;

                  return (
                    <React.Fragment key={day.id || dayIdx}>
                      <tr className={`hover:bg-slate-50/30 transition-colors group align-top ${hasFooterBar ? '' : 'border-b border-slate-200'}`}>
                        {/* Column 1: Itinerary (Day Summary Only) */}
                        <td className={`px-6 border-r border-slate-100 overflow-hidden align-top ${hasFooterBar ? 'pt-8 pb-6' : 'py-8'}`}>
                          <div className="space-y-1.5 min-w-0">
                            <div className="flex items-center justify-between gap-1.5 min-w-0">
                              <div className="flex items-baseline gap-2.5 min-w-0">
                                <span className="text-[14px] sm:text-[15px] font-bold text-slate-800 shrink-0">
                                  Day {dayNum}
                                </span>
                                {formattedDate && (
                                  <span className="text-xs text-slate-500 font-normal truncate">
                                    {formattedDate}
                                  </span>
                                )}
                              </div>
                              {isEditingItinerary && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveDay(dayIdx)}
                                  className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-rose-500 transition-opacity p-0.5 rounded cursor-pointer shrink-0"
                                  title={`Delete Day ${dayNum}`}
                                >
                                  <Trash2 size={13} />
                                </button>
                              )}
                            </div>

                            {/* Second Line: Day Title (Editable in Edit Mode) */}
                            {isEditingItinerary ? (
                              <div className="pt-0.5">
                                <input
                                  type="text"
                                  value={day.day_title !== undefined ? day.day_title : (day.title && day.title !== `Day ${dayNum}` ? day.title : '')}
                                  onChange={(e) => handleDayTitleChange(dayIdx, e.target.value)}
                                  onBlur={() => handleDayTitleBlur(dayIdx)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.target.blur();
                                    }
                                  }}
                                  placeholder="Day title (e.g. Arrival & Shopping)"
                                  className="w-full text-xs text-slate-700 bg-white hover:bg-slate-50 focus:bg-white border border-slate-200 focus:border-[#067978] rounded px-2 py-1 outline-none transition-all placeholder:text-slate-400 font-normal shadow-2xs"
                                />
                              </div>
                            ) : (
                              (day.day_title || (day.title && day.title !== `Day ${dayNum}`)) && (
                                <div className="text-xs text-slate-500 font-normal truncate max-w-full" title={day.day_title || day.title}>
                                  {day.day_title || day.title}
                                </div>
                              )
                            )}
                          </div>
                        </td>

                        {/* Column 2: Events (Events Only) */}
                        <td className={`px-6 border-r border-slate-100 overflow-hidden align-top ${hasFooterBar ? 'pt-8 pb-6' : 'py-8'}`}>
                          <div className="space-y-4 min-w-0">
                            {visibleEvents.length > 0 ? (
                              visibleEvents.map((evt, evtIdx) => {
                                const eventTitle = evt.title || evt.activity || 'Sightseeing Event';
                                const eventTime = evt.time || '';

                                return (
                                  <div 
                                    key={evt.id || `${dayIdx}_${evtIdx}`}
                                    className="pb-3.5 border-b border-slate-100/80 last:border-b-0 last:pb-0 min-w-0 space-y-1.5"
                                  >
                                    {/* Line 1: Title (and Edit/Delete if editing) */}
                                    <div className="flex items-center justify-between gap-3 min-w-0">
                                      <div
                                        className={`text-[13px] font-normal text-slate-700 min-w-0 ${
                                          isExpanded ? 'break-words leading-snug' : 'truncate'
                                        }`}
                                        title={eventTitle}
                                      >
                                        {eventTitle}
                                      </div>

                                      {isEditingItinerary && (
                                        <div className="flex items-center gap-1.5 shrink-0 text-[11px]">
                                          <button
                                            type="button"
                                            onClick={() => setParticularModalState({
                                              dayIdx,
                                              evtIdx,
                                              isNew: false,
                                              currentParticular: evt,
                                              dayNumber: dayNum
                                            })}
                                            className="font-normal text-[#067978] hover:underline cursor-pointer shrink-0"
                                          >
                                            Edit
                                          </button>
                                          <span className="text-slate-300">·</span>
                                          <button
                                            type="button"
                                            onClick={() => handleDeleteParticular(dayIdx, evtIdx)}
                                            className="font-normal text-slate-400 hover:text-rose-600 hover:underline cursor-pointer shrink-0"
                                          >
                                            Delete
                                          </button>
                                        </div>
                                      )}
                                    </div>

                                    {/* Line 2: Scheduled Time (if added) */}
                                    {eventTime && (
                                      <div className="text-xs text-slate-400 font-normal">
                                        {eventTime}
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            ) : (
                              <p className="text-xs text-slate-400">
                                No events added
                              </p>
                            )}

                            {isEditingItinerary && (
                              <div className="flex items-center justify-end pt-2">
                                <button
                                  type="button"
                                  onClick={() => setParticularModalState({
                                    dayIdx,
                                    evtIdx: dayEvents.length,
                                    isNew: true,
                                    currentParticular: null,
                                    dayNumber: dayNum
                                  })}
                                  className="inline-flex items-center gap-1 text-xs font-normal text-[#067978] hover:text-[#056564] hover:underline cursor-pointer"
                                >
                                  <Plus size={12} />
                                  <span>Add event</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Column 3: Driver (Drivers Only) */}
                        <td className={`px-6 border-r border-slate-100 overflow-hidden align-top ${hasFooterBar ? 'pt-8 pb-6' : 'py-8'}`}>
                          <div className="space-y-3 min-w-0">
                            {dayDrivers.length > 0 ? (
                              visibleDrivers.map((c, dIdx) => (
                                <div
                                  key={c.id || c._id || dIdx}
                                  className="pb-2.5 border-b border-slate-100/80 last:border-b-0 last:pb-0 min-w-0 space-y-1"
                                >
                                  {/* Line 1: Driver Name */}
                                  <div
                                    className={`text-[13px] font-medium text-slate-800 min-w-0 ${
                                      isDriversExpanded ? 'break-words leading-snug' : 'truncate'
                                    }`}
                                    title={c.name}
                                  >
                                    {c.name}
                                  </div>

                                  {/* Line 2: Phone */}
                                  {c.number && (
                                    <div className="text-xs text-slate-500 font-mono">
                                      {c.number}
                                    </div>
                                  )}

                                  {/* Line 3: Actions */}
                                  <div className="pt-0.5 flex items-center gap-2.5 flex-wrap">
                                    {isEditingItinerary ? (
                                      <>
                                        <button
                                          type="button"
                                          onClick={() => setContactModalState({
                                            mode: 'driver',
                                            dayIdx,
                                            dayNumber: dayNum,
                                            initialContact: c
                                          })}
                                          className="font-normal text-xs text-[#067978] hover:underline cursor-pointer"
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleDeleteContact(dayIdx, c.id || c._id)}
                                          className="font-normal text-xs text-rose-500 hover:text-rose-600 hover:underline cursor-pointer"
                                        >
                                          Remove
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          type="button"
                                          onClick={() => setViewContactModalState({
                                            ...c,
                                            dayNumber: c.day_number || dayNum,
                                            dayIdx
                                          })}
                                          className="font-normal text-xs text-[#067978] hover:underline cursor-pointer"
                                        >
                                          View
                                        </button>
                                        {dIdx === visibleDrivers.length - 1 && (
                                          <button
                                            type="button"
                                            onClick={() => setContactModalState({
                                              mode: 'driver',
                                              dayIdx,
                                              dayNumber: dayNum,
                                              initialContact: null
                                            })}
                                            className="inline-flex items-center gap-1 text-xs font-normal text-[#067978] hover:text-[#056564] hover:underline cursor-pointer"
                                          >
                                            <Plus size={12} />
                                            <span>Add Driver</span>
                                          </button>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="space-y-1">
                                <p className="text-xs text-slate-400">
                                  No driver assigned
                                </p>
                                <div className="pt-0.5">
                                  <button
                                    type="button"
                                    onClick={() => setContactModalState({
                                      mode: 'driver',
                                      dayIdx,
                                      dayNumber: dayNum,
                                      initialContact: null
                                    })}
                                    className="inline-flex items-center gap-1 text-xs font-normal text-[#067978] hover:text-[#056564] hover:underline cursor-pointer"
                                  >
                                    <Plus size={12} />
                                    <span>Add Driver</span>
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* In Edit mode, show Add Driver button if drivers already exist */}
                            {isEditingItinerary && dayDrivers.length > 0 && (
                              <div className="pt-1">
                                <button
                                  type="button"
                                  onClick={() => setContactModalState({
                                    mode: 'driver',
                                    dayIdx,
                                    dayNumber: dayNum,
                                    initialContact: null
                                  })}
                                  className="inline-flex items-center gap-1 text-xs font-normal text-[#067978] hover:text-[#056564] hover:underline cursor-pointer"
                                >
                                  <Plus size={12} />
                                  <span>Add Driver</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Column 4: Tickets */}
                        <td className={`px-6 overflow-hidden align-top ${hasFooterBar ? 'pt-8 pb-6' : 'py-8'}`}>
                          <div className="space-y-3 min-w-0">
                            {dayTickets.length > 0 ? (
                              visibleTickets.map((ticket, tIdx) => (
                                <div
                                  key={ticket.id || ticket._id || tIdx}
                                  className="pb-2.5 border-b border-slate-100/80 last:border-b-0 last:pb-0 min-w-0 space-y-1"
                                >
                                  {/* Line 1: Ticket Name */}
                                  <div
                                    className={`text-[13px] font-normal text-slate-700 min-w-0 ${
                                      isTicketsExpanded ? 'break-words leading-snug' : 'truncate'
                                    }`}
                                    title={ticket.name}
                                  >
                                    {ticket.name}
                                  </div>

                                  {/* Line 2: Actions */}
                                  <div className="pt-0.5 flex items-center gap-2.5 flex-wrap">
                                    {isEditingItinerary ? (
                                      <>
                                        <button
                                          type="button"
                                          onClick={() => setViewTicketModalState({
                                            ...ticket,
                                            dayNumber: dayNum,
                                            dayIdx
                                          })}
                                          className="font-normal text-xs text-[#067978] hover:underline cursor-pointer"
                                        >
                                          View
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => setTicketModalState({
                                            dayIdx,
                                            dayNumber: dayNum,
                                            initialTicket: ticket
                                          })}
                                          className="font-normal text-xs text-[#067978] hover:underline cursor-pointer"
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveTicket(dayIdx, ticket.id || ticket._id)}
                                          className="font-normal text-xs text-rose-500 hover:text-rose-600 hover:underline cursor-pointer"
                                        >
                                          Remove
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          type="button"
                                          onClick={() => setViewTicketModalState({
                                            ...ticket,
                                            dayNumber: dayNum,
                                            dayIdx
                                          })}
                                          className="font-normal text-xs text-[#067978] hover:underline cursor-pointer"
                                        >
                                          View
                                        </button>
                                        {tIdx === visibleTickets.length - 1 && (
                                          <button
                                            type="button"
                                            onClick={() => setTicketModalState({
                                              dayIdx,
                                              dayNumber: dayNum,
                                              initialTicket: null
                                            })}
                                            className="inline-flex items-center gap-1 text-xs font-normal text-[#067978] hover:text-[#056564] hover:underline cursor-pointer"
                                          >
                                            <Plus size={12} />
                                            <span>Add Ticket</span>
                                          </button>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="space-y-1">
                                <p className="text-xs text-slate-400">
                                  No tickets attached yet
                                </p>
                                <div className="pt-0.5">
                                  <button
                                    type="button"
                                    onClick={() => setTicketModalState({
                                      dayIdx,
                                      dayNumber: dayNum,
                                      initialTicket: null
                                    })}
                                    className="inline-flex items-center gap-1 text-xs font-normal text-[#067978] hover:text-[#056564] hover:underline cursor-pointer"
                                  >
                                    <Plus size={12} />
                                    <span>Add Ticket</span>
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* In Edit mode, show Add Ticket button if tickets already exist */}
                            {isEditingItinerary && dayTickets.length > 0 && (
                              <div className="pt-1">
                                <button
                                  type="button"
                                  onClick={() => setTicketModalState({
                                    dayIdx,
                                    dayNumber: dayNum,
                                    initialTicket: null
                                  })}
                                  className="inline-flex items-center gap-1 text-xs font-normal text-[#067978] hover:text-[#056564] hover:underline cursor-pointer"
                                >
                                  <Plus size={12} />
                                  <span>Add Ticket</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* Day Footer Controls: Column-aligned Centered View More Bar */}
                      {hasFooterBar && (
                        <tr className="hover:bg-slate-50/30 transition-colors border-b border-slate-200">
                          {/* Column 1: Spacer under Itinerary */}
                          <td className="py-3 px-6 border-r border-slate-100 align-middle" />

                          {/* Column 2: Events Centered View More */}
                          <td className="py-3 px-6 border-r border-slate-100 text-center align-middle">
                            {hasMultipleEvents && (
                              <button
                                type="button"
                                onClick={() => toggleDayParticulars(dayIdx)}
                                className="inline-flex items-center justify-center gap-1 text-xs font-normal text-[#067978] hover:underline cursor-pointer mx-auto"
                              >
                                {isExpanded ? (
                                  <>
                                    <ChevronUp size={13} />
                                    <span>Show fewer events</span>
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown size={13} />
                                    <span>View {hiddenCount} more {hiddenCount === 1 ? 'event' : 'events'}</span>
                                  </>
                                )}
                              </button>
                            )}
                          </td>

                          {/* Column 3: Driver Centered View More */}
                          <td className="py-3 px-6 border-r border-slate-100 text-center align-middle">
                            {hasMultipleDrivers && (
                              <button
                                type="button"
                                onClick={() => toggleDayDrivers(dayIdx)}
                                className="inline-flex items-center justify-center gap-1 text-xs font-normal text-[#067978] hover:underline cursor-pointer mx-auto"
                              >
                                {isDriversExpanded ? (
                                  <>
                                    <ChevronUp size={13} />
                                    <span>Show fewer drivers</span>
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown size={13} />
                                    <span>View {hiddenDriversCount} more {hiddenDriversCount === 1 ? 'driver' : 'drivers'}</span>
                                  </>
                                )}
                              </button>
                            )}
                          </td>

                          {/* Column 4: Tickets Centered View More */}
                          <td className="py-3 px-6 text-center align-middle">
                            {hasMultipleTickets && (
                              <button
                                type="button"
                                onClick={() => toggleDayTickets(dayIdx)}
                                className="inline-flex items-center justify-center gap-1 text-xs font-normal text-[#067978] hover:underline cursor-pointer mx-auto"
                              >
                                {isTicketsExpanded ? (
                                  <>
                                    <ChevronUp size={13} />
                                    <span>Show fewer tickets</span>
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown size={13} />
                                    <span>View {hiddenTicketsCount} more {hiddenTicketsCount === 1 ? 'ticket' : 'tickets'}</span>
                                  </>
                                )}
                              </button>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Itinerary Contact Modal (Single Source Reusable Modal) */}
      <ItineraryContactModal
        isOpen={Boolean(contactModalState)}
        onClose={() => setContactModalState(null)}
        mode={contactModalState?.mode || 'general'}
        dayNumber={contactModalState?.dayNumber}
        dayEvents={contactModalState && typeof contactModalState.dayIdx === 'number' ? (itineraryDays[contactModalState.dayIdx]?.events || []) : []}
        itinerary={formData?.itinerary || []}
        tourStartDate={formData?.tour_info?.start_date}
        contacts={formData?.contact_details || []}
        assignedContacts={contactModalState && typeof contactModalState.dayIdx === 'number' ? getDayDrivers(itineraryDays[contactModalState.dayIdx], contactModalState.dayIdx) : []}
        onSaveContact={(contactData, isEdit) => {
          if (contactModalState) {
            handleSaveContact(contactModalState.dayIdx, contactData, isEdit);
          }
        }}
        onDeleteContact={(contactId) => {
          if (contactModalState) {
            handleDeleteContact(contactModalState.dayIdx, contactId);
          }
        }}
        initialContact={contactModalState?.initialContact}
      />

      {/* Ticket / Pass Modal (Multi-event assignment + Important Documents sync) */}
      <TicketModal
        isOpen={Boolean(ticketModalState)}
        onClose={() => setTicketModalState(null)}
        dayNumber={ticketModalState?.dayNumber}
        dayEvents={ticketModalState ? (itineraryDays[ticketModalState.dayIdx]?.events || []) : []}
        assignedTickets={ticketModalState ? getDayTickets(itineraryDays[ticketModalState.dayIdx], ticketModalState.dayIdx) : []}
        onSave={(ticketData, isEdit) => {
          if (ticketModalState) {
            handleSaveTicket(ticketModalState.dayIdx, ticketData, isEdit);
          }
        }}
        onRemove={(ticketId) => {
          if (ticketModalState) {
            handleRemoveTicket(ticketModalState.dayIdx, ticketId);
          }
        }}
        currentTicket={ticketModalState?.initialTicket}
      />

      {/* Particular Edit / Add Modal */}
      <ParticularModal
        isOpen={Boolean(particularModalState)}
        onClose={() => setParticularModalState(null)}
        onSave={handleSaveParticular}
        onDelete={() => {
          if (particularModalState) {
            handleDeleteParticular(particularModalState.dayIdx, particularModalState.evtIdx);
          }
        }}
        isNew={particularModalState?.isNew}
        currentParticular={particularModalState?.currentParticular}
        dayNumber={particularModalState?.dayNumber}
      />

      {/* View Contact Details Modal */}
      <ViewContactModal
        isOpen={Boolean(viewContactModalState)}
        onClose={() => setViewContactModalState(null)}
        contact={viewContactModalState}
        onEdit={(contact) => {
          setViewContactModalState(null);
          setContactModalState({
            mode: contact.label === 'Driver' || contact.role === 'Driver' ? 'driver' : 'general',
            dayIdx: contact.dayIdx,
            dayNumber: contact.dayNumber,
            initialContact: contact
          });
        }}
      />

      {/* View Event Details Modal */}
      <ViewEventModal
        isOpen={Boolean(viewEventModalState)}
        onClose={() => setViewEventModalState(null)}
        event={viewEventModalState}
        onEdit={(evt) => {
          setViewEventModalState(null);
          setParticularModalState({
            dayIdx: evt.dayIdx,
            evtIdx: evt.evtIdx,
            isNew: false,
            currentParticular: evt,
            dayNumber: evt.dayNumber
          });
        }}
      />

      {/* View Ticket Details Modal */}
      <ViewTicketModal
        isOpen={Boolean(viewTicketModalState)}
        onClose={() => setViewTicketModalState(null)}
        ticket={viewTicketModalState}
        onEdit={(ticket) => {
          setViewTicketModalState(null);
          setTicketModalState({
            dayIdx: ticket.dayIdx,
            dayNumber: ticket.dayNumber,
            initialTicket: ticket
          });
        }}
      />

    </div>
  );
};

export default ItineraryTableView;
